import { Component, Input, OnInit, ViewChild, OnDestroy, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Message, User } from '@progress/kendo-angular-conversational-ui';
import { TextAreaComponent } from '@progress/kendo-angular-inputs';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { ChatService } from 'src/app/core/service/chat.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import moment from 'moment';
import { cloneDeep, differenceBy, uniqBy } from 'lodash';
import { Subscription } from 'rxjs';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';

@Component({
  selector: 'app-chat-history',
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss']
})
export class ChatHistoryComponent implements OnInit, OnDestroy
{
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;
  @ViewChild('messageBoxInput', { static: false }) public messageBoxInput: TextAreaComponent;

  @Input() public isWindow = false;
  @Input() public windowHeight: number;
  @Input() public users = [];

  public selectedChat = null;
  public innerInputFocus = false;
  public currentUser: User = null;
  public chatHistory: Message[] = [];
  public textMessage = '';
  public textChatUpdated = '';
  public chatHeight = '100vh';
  public deleteConfirmationOpened = false;
  public deleteHistoryItem = null;

  private messagingSubscription: Subscription;

  constructor(
    private chatService: ChatService,
    private authenticationService: AuthenticationService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private translate: TranslateService,
    private userService: UserService)
  {
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'reloadChatHistory')
        {
          this.loadChatHistory(message.data.unreadCount > 0);  // If unreadCount is 0, that means a chat history was deleted
        }
      }
    });
  }

  @Input() public set chat(value: any)
  {
    this.selectedChat = value;
    this.loadChatHistory(false);
  }

  public ngOnInit(): void
  {
    this.translate.get(['CHAT-MESSAGE', 'CHAT-UPDATED']).subscribe({
      next: (res) =>
      {
        this.textChatUpdated = res['CHAT-UPDATED'];
        this.textMessage = res['CHAT-MESSAGE'];
        if (this.authenticationService.currentUser)
        {
          this.userService.getUserById(this.authenticationService.currentUser.id, true).subscribe({
            next: (user) =>
            {
              this.currentUser = {
                id: this.authenticationService.currentUser.id,
                name: this.authenticationService.currentUser.name,
                avatarUrl: user.photo ? `data:image/jpeg;base64,${user.photo}` : null
              };
            }
          });
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public deleteHistory(item: any): void
  {
    this.deleteHistoryItem = item;
    this.deleteConfirmationOpened = true;
  }

  public sendMessage(): void
  {
    const messageBox = this.messageBoxInput;
    const newMessage = messageBox.value;

    if (!newMessage)
    {
      return;
    }

    const messageDateTime = moment();
    const message: any = {
      id: null,
      author: this.currentUser,
      content: newMessage,
      chatId: this.selectedChat.id,
      status: null,
      timestamp: messageDateTime.toDate(),
      updated: false
    };

    // Create a persisted message
    const chatMessageToPersist: any = cloneDeep(message);
    delete chatMessageToPersist.author.avatarUrl;  // Do not send avatar data to server
    delete chatMessageToPersist.timestamp;
    chatMessageToPersist.chatDateTime = messageDateTime.utc().toISOString();

    this.chatService.addChatHistory(this.selectedChat.id, chatMessageToPersist).subscribe({
      next: (data: any) =>
      {
        message.id = data.id;   // Refresh message id
        delete message.chatId;  // Remove chatId that was required on addChatHistory only
        delete message.chatDateTime;
        this.chatHistory = [...this.chatHistory, message];
        messageBox.value = null;
        messageBox.focus();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public clearValue(): void
  {
    if (!this.innerInputFocus)
    {
      this.messageBoxInput.value = null;
      this.messageBoxInput.focus();
      this.innerInputFocus = true;
    }
  }

  public onKeyDown(e: KeyboardEvent): void
  {
    const isEnter = e.key === 'Enter';

    if (!isEnter)
    {
      return;
    }

    const newLine = e.metaKey || e.ctrlKey;
    const enterOnly = !(e.shiftKey || e.metaKey || e.ctrlKey);

    if (enterOnly && this.innerInputFocus)
    {
      e.preventDefault();
      this.sendMessage();
    }

    if (newLine)
    {
      this.messageBoxInput.value += `\r\n`;
    }
  }

  public onInputFocus(): void
  {
    this.innerInputFocus = true;
  }

  public onInputBlur(): void
  {
    this.innerInputFocus = false;
  }

  public onResized(): void
  {
    const adjustHeight = this.isWindow ? 64 : 138;
    this.chatHeight = `${window.innerHeight - adjustHeight}px`;
  }

  public onDelete(): void
  {
    this.chatService.deleteChatHistory(this.deleteHistoryItem.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('CHAT-MESSAGE-DELETED');
        this.deleteConfirmationOpened = false;
        this.chatHistory = this.chatHistory.filter((x: any) => x.id !== this.deleteHistoryItem.id);
      },
      error: (errorResponse) =>
      {
        this.deleteConfirmationOpened = false;
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public showTooltip(e: ElementRef): void
  {
    this.tooltipDir.toggle(e, true);
  }
  
  public hideTooltip(): void
  {
    this.tooltipDir.hide();
  }

  public isCurrentUser = (item: any): boolean =>
    (item?.author?.id === this.authenticationService.currentUser.id);

  private loadChatHistory(callRefreshChatEvent: boolean): void
  {
    let chatHistory = [];
    if (this.selectedChat)
    {
      this.chatService.getChatHistory(this.selectedChat.id).subscribe({
        next: (data) =>
        {
          data.forEach((x: any) =>
          {
            x.status = x.updated ? this.textChatUpdated : null;
            x.timestamp = new Date(x.chatDateTime);
            delete x.chatDateTime;
            // Set user photo
            const userFound = this.users.find(user => user.id === x.author.id);
            x.author.avatarUrl = userFound.photo ? `data:image/jpeg;base64,${userFound.photo}` : null;
          });

          chatHistory = data;
          if (this.messageBoxInput)
          {
            this.clearValue();
          }

          // When refresh from a subscription, we need to call the refresh for the current selected chat row.
          if (callRefreshChatEvent)
          {
            // If chat is selected, callRefreshChatEvent will be true and we inject just the new posts
            const newPosts: Message[] = differenceBy(chatHistory, this.chatHistory, 'id');
            let newHistory = [];
            newPosts.forEach(chat => { newHistory = [...this.chatHistory, chat]; });
            this.chatHistory = uniqBy(newHistory, 'id');

            this.messengerService.sendMessage(new MessageSubject('refreshChatUnread',
              [{
                chatId: this.selectedChat.id,
                unreadCount: 0
              }]));
          }
          else
          {
            this.chatHistory = chatHistory;
          }
        }
      });
    }
    else
    {
      this.chatHistory = chatHistory;
    }
  }
}
